// @flow
import memoize from 'memoize-one';
import transformRequiredImmunizations from '../util/immunizationUtil';

import {
  FETCH_PATIENT_DATA_LOADING,
  FETCH_PATIENT_DATA_SUCCESS,
  FETCH_PATIENT_DATA_ERROR,
  ACTIVATE_PRINT_PATIENT,
  DEACTIVATE_PRINT_PATIENT,
} from '../actions/patientActions';

import {
  NURSE_NOTE_TYPE,
  PROVIDER_NOTE_TYPE,
  SYMPTOM_ANALYSIS_TYPE,
  SYSTEM_ASSESSMENT_TYPE,
} from '../components/patientPages/progressNote/progressNoteTypes';

const memProblemIdToMeds = memoize((medications) => {
  const problemIdToMeds = {};
  for (let i = 0; i < medications.length; i++) {
    const medication = medications[i];
    if (medication.problemId in problemIdToMeds) {
      problemIdToMeds[medication.problemId].push(medication);
    } else {
      problemIdToMeds[medication.problemId] = [medication];
    }
  }
  return problemIdToMeds;
});

function buildProblemIdToMeds(data: Object) {
  const {
    acuteMedications,
    chronicMedications,
  } = data;
  const medications = acuteMedications.concat(chronicMedications);
  return memProblemIdToMeds(medications);
}

const memProblemIdToInterventions = memoize((interventions) => {
  const problemIdToInterventions = {};
  for (let i = 0; i < interventions.length; i++) {
    const intervention = interventions[i];
    if (intervention.problemId in problemIdToInterventions) {
      problemIdToInterventions[intervention.problemId].push(intervention);
    } else {
      problemIdToInterventions[intervention.problemId] = [intervention];
    }
  }
  return problemIdToInterventions;
});

function buildProblemIdToInterventions(data: Object) {
  const {
    acuteInterventions,
    chronicInterventions,
  } = data;
  const interventions = acuteInterventions.concat(chronicInterventions);
  return memProblemIdToInterventions(interventions);
}

// memoization and redux store needed to access problem object from problemId from URL params
const memProblemIdToProblem = memoize((problems) => {
  const problemIdToProblem = {};
  for (let i = 0; i < problems.length; i++) {
    const problem = problems[i];
    problemIdToProblem[problem._id] = problem;
  }
  return problemIdToProblem;
});

function buildProblemIdToProblem(data: Object) {
  let {
    acuteProblems,
    chronicProblems,
  } = data;
  acuteProblems = acuteProblems.map(problem => ({ ...problem, acute: true }));
  chronicProblems = chronicProblems.map(problem => ({ ...problem, chronic: true }));
  const problems = acuteProblems.concat(chronicProblems);
  return memProblemIdToProblem(problems);
}

const memProblemIdToProgressNotes = memoize((notes) => {
  notes.sort((a, b) => {
    if (!a.date) {
      return 1;
    }
    if (!b.date) {
      return -1;
    }
    if (a.date < b.date) {
      return 1;
    }
    if (b.date < a.date) {
      return -1;
    }
    return 0;
  });
  const problemIdToNotes = {};
  for (let i = 0; i < notes.length; i++) {
    const note = notes[i];
    if (note.problemId in problemIdToNotes) {
      problemIdToNotes[note.problemId].push(note);
    } else {
      problemIdToNotes[note.problemId] = [note];
    }
  }
  return problemIdToNotes;
});

function buildProblemIdToProgressNotes(data: Object) {
  let {
    nurseNotes,
    providerNotes,
    symptomAnalyses,
    systemAssessments,
  } = data.progressNotes;

  nurseNotes = nurseNotes.map(note => ({
    type: NURSE_NOTE_TYPE,
    ...note,
  }));
  providerNotes = providerNotes.map(note => ({
    type: PROVIDER_NOTE_TYPE,
    ...note,
  }));
  symptomAnalyses = symptomAnalyses.map(note => ({
    type: SYMPTOM_ANALYSIS_TYPE,
    ...note,
  }));
  systemAssessments = systemAssessments.map(note => ({
    type: SYSTEM_ASSESSMENT_TYPE,
    ...note,
  }));
  const notes = nurseNotes.concat(providerNotes).concat(symptomAnalyses).concat(systemAssessments);
  return memProblemIdToProgressNotes(notes);
}

function getRecentHeightAndWeight(data: Object) {
  let mostRecentHeight = '--';
  let mostRecentWeight = '--';
  let heightDate;
  let weightDate;
  const {
    wellChildChecks, infantMeasurements, childMeasurements, requiredMeasurements,
  } = data;
  for (let i = 0; i < wellChildChecks.length; i++) {
    if ((!heightDate || wellChildChecks[i].date > heightDate) && wellChildChecks[i].vitals.length) {
      mostRecentHeight = wellChildChecks[i].vitals.length;
      heightDate = wellChildChecks[i].date;
    }
    if ((!weightDate || wellChildChecks[i].date > weightDate) && wellChildChecks[i].vitals.weight) {
      mostRecentWeight = wellChildChecks[i].vitals.weight;
      weightDate = wellChildChecks[i].date;
    }
  }
  for (let i = 0; i < infantMeasurements.length; i++) {
    if ((!heightDate || infantMeasurements[i].date > heightDate) && infantMeasurements[i].length) {
      mostRecentHeight = infantMeasurements[i].length;
      heightDate = infantMeasurements[i].date;
    }
    if ((!weightDate || infantMeasurements[i].date > weightDate) && infantMeasurements[i].weight) {
      mostRecentWeight = infantMeasurements[i].weight;
      weightDate = infantMeasurements[i].date;
    }
  }
  for (let i = 0; i < childMeasurements.length; i++) {
    if ((!heightDate || childMeasurements[i].date > heightDate) && childMeasurements[i].length) {
      mostRecentHeight = childMeasurements[i].length;
      heightDate = childMeasurements[i].date;
    }
    if ((!weightDate || childMeasurements[i].date > weightDate) && childMeasurements[i].weight) {
      mostRecentWeight = childMeasurements[i].weight;
      weightDate = childMeasurements[i].date;
    }
  }
  for (let i = 0; i < requiredMeasurements.length; i++) {
    if ((!heightDate || requiredMeasurements[i].date > heightDate) && requiredMeasurements[i].length) {
      mostRecentHeight = requiredMeasurements[i].length;
      heightDate = requiredMeasurements[i].date;
    }
    if ((!weightDate || requiredMeasurements[i].date > weightDate) && requiredMeasurements[i].weight) {
      mostRecentWeight = requiredMeasurements[i].weight;
      weightDate = requiredMeasurements[i].date;
    }
  }

  return {
    mostRecentHeight,
    mostRecentWeight,
  };
}

export default (state: Object = {}, action: Object) => {
  switch (action.type) {
    case FETCH_PATIENT_DATA_LOADING:
      return {
        ...state,
        status: 'LOADING',
      };
    case FETCH_PATIENT_DATA_SUCCESS:
      return {
        ...state,
        status: 'SUCCESS',
        data: {
          ...action.data,
          ...getRecentHeightAndWeight(action.data),
          problemIdToMeds: buildProblemIdToMeds(action.data),
          problemIdToInterventions: buildProblemIdToInterventions(action.data),
          problemIdToProblem: buildProblemIdToProblem(action.data),
          problemIdToProgressNotes: buildProblemIdToProgressNotes(action.data),
          requiredImmunizations: transformRequiredImmunizations(action.data),
        },
      };
    case FETCH_PATIENT_DATA_ERROR:
      return {
        ...state,
        status: 'ERROR',
        error: action.error,
      };
    case ACTIVATE_PRINT_PATIENT:
      return {
        ...state,
        print: true,
      };
    case DEACTIVATE_PRINT_PATIENT:
      return {
        ...state,
        print: false,
      };
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./client/src/reducers/patientReducer.js